import React, { ReactNode, useEffect } from 'react';
import { useQuery } from 'react-query';

import { message } from 'antd';

import { getUserCommunities } from 'apis/CommunityAPI';
import { getCompanies } from 'apis/UserAPI';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  handleSetLoader,
  handleSetSelectedClient,
  handleSetSelectedCompany,
  handleStoreCommunityMap,
  handleStoreCompanies,
  handleStoreUserCommunities,
  handleStoreUserCommunitiesCompetitors,
} from 'store/slices/globalSlice';
import { processFullHomeCommunities } from 'utils/data/communities';

import ActionCenter from '../ActionCenter';
import { RootContext } from './RootContext';

interface Props {
  children: ReactNode;
}

const RootLoaders = ({ children }: Props) => {
  const dispatch = useAppDispatch();

  const [messageApi, contextHolder] = message.useMessage();

  const { selectedClient, selectedCompany } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);

  // Set up available User and Company information for Client
  useEffect(() => {
    if (currentUser && !currentUser.isSuperuser) {
      dispatch(handleSetLoader(true));
      dispatch(
        handleSetSelectedClient({
          id: currentUser.PAUser_Id,
          name: currentUser.name,
          email: currentUser.email,
          level: currentUser.level,
          company: currentUser.company,
        })
      );

      if (currentUser.company) {
        dispatch(
          handleSetSelectedCompany({
            id: currentUser.company ?? -1,
            name: currentUser.companyName ?? 'Unknown Company',
            primary_email: currentUser.companyPrimaryEmail,
          })
        );
      } else {
        dispatch(handleSetSelectedCompany(undefined));
      }
    }
  }, [currentUser, dispatch]);

  // Perform fetching of companies for Admin users
  useQuery({
    queryKey: ['global', 'company', 'list', currentUser?.user_id],
    queryFn: () => getCompanies({ page_size: PAGINATION_PAGE_SIZE.EXTRA_LARGE }),
    onSuccess: (response) => {
      dispatch(handleStoreCompanies(response?.results ?? []));
    },
    enabled: Boolean(currentUser),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Perform fetching for User Communities and Competitors
  const { refetch: refreshCommunities } = useQuery({
    queryKey: ['user-communities', currentUser?.user_id, selectedClient?.id, selectedCompany?.id],
    queryFn: () => getUserCommunities({}),
    onSuccess: (response) => {
      const { communities, competitors, communityMap } = processFullHomeCommunities(response);

      dispatch(handleStoreCommunityMap(communityMap));
      dispatch(handleStoreUserCommunities(communities));
      dispatch(handleStoreUserCommunitiesCompetitors(competitors));
    },
    onSettled: () => {
      dispatch(handleSetLoader(false));
    },
    enabled: !!currentUser && !currentUser.isSuperuser && Boolean(selectedClient?.id),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const showMessage = (type: 'success' | 'info' | 'warning' | 'error', mgs: string) => {
    messageApi.open({
      type: type,
      content: mgs,
    });
  };

  return (
    <RootContext.Provider value={{ refreshCommunities, showMessage }}>
      {contextHolder}
      {children}
      <ActionCenter />
    </RootContext.Provider>
  );
};

export default RootLoaders;
